import React from 'react';
import { SEVERITY_TEXT, formatDegrees, formatRange, scanDegreesOf } from '../lib/report/scanDegrees';

const SEVERITY_CLASS = {
  normal: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  mild: 'bg-amber-50 text-amber-700 ring-amber-200',
  moderate: 'bg-orange-50 text-orange-700 ring-orange-200',
  severe: 'bg-red-50 text-red-700 ring-red-200',
};

function Grade({ severity }) {
  if (!severity) return <span className="text-gray-400">—</span>;
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${SEVERITY_CLASS[severity] ?? 'bg-gray-50 text-gray-600 ring-gray-200'}`}>
      {SEVERITY_TEXT[severity] ?? severity}
    </span>
  );
}

function SideTable({ title, rows }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      <table className="w-full table-fixed text-xs">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="w-[28%] py-1 pr-2 font-medium">Parameter</th>
            <th className="w-[22%] py-1 pr-2 font-medium">Left</th>
            <th className="w-[22%] py-1 pr-2 font-medium">Right</th>
            <th className="py-1 font-medium">Reference</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map(r => (
            <tr key={r.label}>
              <td className="py-1 pr-2 text-gray-700">{r.label}</td>
              <td className="py-1 pr-2 tabular-nums text-gray-900">{r.left}</td>
              <td className="py-1 pr-2 tabular-nums text-gray-900">{r.right}</td>
              <td className="py-1 text-gray-500">{r.reference ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Every angle a scan measured, in degrees, with its reference range and grade
 * (lib/report/scanDegrees) — the numbers behind the arch and foot type the
 * patient list shows.
 */
export default function ScanDegrees({ scan }) {
  const { groups, foot, walk, measured, outside } = scanDegreesOf(scan);

  if (!groups.length && !foot && !walk) {
    return <p className="text-xs text-gray-500">No measurements were recorded in this scan.</p>;
  }

  return (
    <div className="space-y-4" data-testid={`scan-degrees-${scan.id}`}>
      {measured > 0 && (
        <p className="text-xs text-gray-600">
          <span className="font-semibold text-gray-900">{measured}</span> angles measured ·{' '}
          <span className={outside ? 'font-semibold text-orange-700' : 'font-semibold text-emerald-700'}>{outside}</span> outside reference range
        </p>
      )}

      {groups.map((g, i) => (
        <div key={`${g.title}-${i}`}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {g.kind === 'leg' ? 'Leg alignment' : 'Posture'} — {g.title}
          </p>
          {g.unmeasured ? (
            <p className="text-xs text-gray-500">Not measured: the joints could not be read in this capture.</p>
          ) : (
            <>
              {g.rows.length > 0 && (
                <table className="w-full table-fixed text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="w-[46%] py-1 pr-2 font-medium">Measurement</th>
                      <th className="py-1 pr-2 text-right font-medium">Angle</th>
                      <th className="py-1 pr-2 text-right font-medium">Reference</th>
                      <th className="py-1 text-right font-medium">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {g.rows.map((r, j) => (
                      <tr key={`${r.name}-${j}`}>
                        <td className="py-1 pr-2 text-gray-700">{r.name}</td>
                        <td className="py-1 pr-2 text-right font-semibold tabular-nums text-gray-900">{formatDegrees(r.degrees)}</td>
                        <td className="py-1 pr-2 text-right tabular-nums text-gray-500">{formatRange(r.range)}</td>
                        <td className="py-1 text-right"><Grade severity={r.severity} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {g.findings.length > 0 && (
                <ul className="mt-1 list-disc pl-4 text-xs text-gray-700">
                  {g.findings.map(f => <li key={f}>{f}</li>)}
                </ul>
              )}
            </>
          )}
        </div>
      ))}

      {foot && (
        <div className="space-y-2">
          {foot.classification.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Foot classification</p>
              <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs">
                {foot.classification.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt className="text-gray-500">{label}</dt>
                    <dd className="capitalize text-gray-900">{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}
          {foot.plantar.length > 0 && <SideTable title="Plantar — standing" rows={foot.plantar} />}
        </div>
      )}

      {walk && <SideTable title="Walking test" rows={walk} />}
    </div>
  );
}
